import { isDefined } from "@clipboard-health/util-ts";
import { useGetWorkerShifts } from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShifts";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo } from "react";

import { useWorkplaceOpenShiftsData } from "../../../Workplace/OpenShifts/useWorkplaceOpenShiftsData";
import type { ModalWorkplaceData } from "../../types";
import type { ShiftBundle } from "../types";
import { useRecentWorkplaceWorkedShiftCounts } from "../useRecentWorkplaceWorkedShiftCounts";
import { useShiftBundlesConfig } from "../useShiftBundlesConfig";
import { getBundleForShift } from "../utils";

interface UseBundleForShiftProps {
  shiftId: string | undefined;
  workplace: ModalWorkplaceData | undefined;
  enabled: boolean;
}

interface UseBundleForShiftResult {
  bundle: ShiftBundle | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Hook to fetch and calculate a bundle for a specific shift.
 * Use this when bundle data is not available in context (e.g., returning from quiz flow).
 */
export function useBundleForShift(props: UseBundleForShiftProps): UseBundleForShiftResult {
  const { shiftId, workplace, enabled } = props;

  const config = useShiftBundlesConfig();
  const worker = useDefinedWorker();

  const shouldFetch = enabled && isDefined(shiftId) && isDefined(workplace);

  const {
    recentWorkplaceWorkedShiftCounts,
    isLoading: isLoadingRecentWorkplaces,
    isError: isErrorRecentWorkplaces,
  } = useRecentWorkplaceWorkedShiftCounts({
    workerId: worker.userId,
    lookbackDays: config?.enabled ? config.recentlyWorkedThresholdInDays : 0,
    enabled: Boolean(config?.enabled) && shouldFetch,
  });

  const {
    data: { unfilteredWorkplaceShifts: workplaceShifts, isShiftBundlesEnabled },
    isLoading: isLoadingShifts,
    isError: isErrorShifts,
  } = useWorkplaceOpenShiftsData(workplace, { enabled: shouldFetch });

  const {
    shifts: workerShifts,
    isLoading: isLoadingWorkerShifts,
    isErrored: isErroredWorkerShifts,
  } = useGetWorkerShifts({ worker, enabled: shouldFetch });

  const bookedShifts = useMemo(
    () =>
      workerShifts.map((workerShift) => ({
        start: workerShift.start,
        end: workerShift.end,
        workplaceId: workerShift.facility.userId ?? "",
        qualification: workerShift.agentReq,
      })),
    [workerShifts]
  );

  const isLoading = isLoadingShifts || isLoadingWorkerShifts || isLoadingRecentWorkplaces;
  const isError = isErrorShifts || isErroredWorkerShifts || isErrorRecentWorkplaces;

  const bundle = useMemo(() => {
    if (!shouldFetch || !shiftId || isLoading || isError || !isShiftBundlesEnabled) {
      return undefined;
    }

    return getBundleForShift({
      targetShiftId: shiftId,
      workplaceShifts,
      config,
      bookedShifts,
      recentShiftCount: recentWorkplaceWorkedShiftCounts.get(workplace?.id) ?? 0,
    });
  }, [
    shouldFetch,
    shiftId,
    workplace,
    workplaceShifts,
    config,
    bookedShifts,
    isLoading,
    isError,
    isShiftBundlesEnabled,
    recentWorkplaceWorkedShiftCounts,
  ]);

  return {
    bundle,
    isLoading,
    isError,
  };
}
